import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

export default class extends Controller {
  static values = {
    zoom: { type: String, default: "day" },
    startDate: String,
    teamId: Number
  }

  connect() {
    this.blockHandlers = new WeakMap()
    this.setupTooltips()
    this.syncRowHeights()
  }

  disconnect() {
    this.hideTooltip()
    if (this.blockHandlers) {
      this.element.querySelectorAll(".gantt-shift-block").forEach(block => {
        const pair = this.blockHandlers.get(block)
        if (!pair) return
        block.removeEventListener("mouseenter", pair.enter)
        block.removeEventListener("mouseleave", pair.leave)
      })
      this.blockHandlers = null
    }
  }

  setupTooltips() {
    this.element.querySelectorAll(".gantt-shift-block").forEach(block => {
      const enter = (e) => this.showTooltip(e)
      const leave = () => this.hideTooltip()
      block.addEventListener("mouseenter", enter)
      block.addEventListener("mouseleave", leave)
      this.blockHandlers.set(block, { enter, leave })
    })
  }

  showTooltip(event) {
    const block = event.currentTarget
    const content = block.getAttribute("title")
    if (!content) return

    this.hideTooltip()

    const tooltip = document.createElement("div")
    tooltip.className = "gantt-tooltip"
    tooltip.textContent = content
    tooltip.style.position = "fixed"
    tooltip.style.left = `${event.clientX + 10}px`
    tooltip.style.top = `${event.clientY - 30}px`
    tooltip.style.zIndex = "1000"

    document.body.appendChild(tooltip)
    this.currentTooltip = tooltip

    block.dataset.originalTitle = content
    block.removeAttribute("title")
  }

  hideTooltip() {
    if (this.currentTooltip) {
      this.currentTooltip.remove()
      this.currentTooltip = null
    }

    this.element.querySelectorAll("[data-original-title]").forEach(block => {
      block.setAttribute("title", block.dataset.originalTitle)
      delete block.dataset.originalTitle
    })
  }

  setZoom(event) {
    const level = event.currentTarget.dataset.zoom
    this.zoomValue = level
    this.refreshGantt()
  }

  navigate(event) {
    const direction = parseInt(event.currentTarget.dataset.direction)
    const currentDate = new Date(this.startDateValue)

    let daysToMove
    switch (this.zoomValue) {
      case "day":
        daysToMove = 7
        break
      case "week":
        daysToMove = 30
        break
      default:
        daysToMove = 90
    }

    currentDate.setDate(currentDate.getDate() + (daysToMove * direction))
    this.startDateValue = currentDate.toISOString().split("T")[0]
    this.refreshGantt()
  }

  goToToday() {
    this.startDateValue = new Date().toISOString().split("T")[0]
    this.refreshGantt()
  }

  refreshGantt() {
    const url = new URL(window.location.href)
    url.searchParams.set("zoom", this.zoomValue)
    url.searchParams.set("start_date", this.startDateValue)
    if (this.teamIdValue) {
      url.searchParams.set("team_id", this.teamIdValue)
    }
    // Use Turbo.visit instead of window.location.href so Turbo can handle
    // the transition — scroll position is restored on back/forward, Stimulus
    // disconnect hooks fire cleanly, and cached partials reload instantly.
    Turbo.visit(url.toString())
  }

  // Sync sidebar row heights with content rows so names align with shift blocks.
  // The server-side height calc accounts for most of it; this fixes sub-pixel drift.
  syncRowHeights() {
    const sidebarRows = this.element.querySelectorAll(".gantt-sidebar-row")
    const contentRows = this.element.querySelectorAll(".roster-row, .gantt-row")

    sidebarRows.forEach((sidebarRow, i) => {
      if (contentRows[i]) {
        const h = contentRows[i].getBoundingClientRect().height
        sidebarRow.style.height = `${h}px`
        sidebarRow.style.minHeight = `${h}px`
      }
    })
  }
}
